/**
 * Book Settings Modal
 * Modal for editing book-level settings including default font family and size
 */

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

import { Book, apiService } from '../../services/api'
import { logger } from '../../utils/logger'
import { FONTS, FONT_SIZES } from '../editor/editor.constants'

export interface BookSettingsModalProps {
  open: boolean
  onClose: () => void
  book: Book
  onBookUpdated: (updatedBook: Book) => void
}

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'he', label: 'Hebrew' },
  { value: 'ar', label: 'Arabic' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'it', label: 'Italian' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'ru', label: 'Russian' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'zh', label: 'Chinese' },
]

export const BookSettingsModal: React.FC<BookSettingsModalProps> = ({ open, onClose, book, onBookUpdated }) => {
  const [title, setTitle] = useState(book.title)
  const [language, setLanguage] = useState(book.metadata.language || 'en')
  const [visibility, setVisibility] = useState<Book['visibility']>(book.visibility || 'private')
  const [defaultFontFamily, setDefaultFontFamily] = useState(book.settings.defaultFontFamily || 'Huninn')
  const [defaultFontSize, setDefaultFontSize] = useState(book.settings.defaultFontSize || '12')
  const [titleFontFamily, setTitleFontFamily] = useState(book.settings.titleFontFamily || 'SBL Hebrew')
  const [structure, setStructure] = useState<'chapters' | 'flat'>(book.settings.structure ?? 'chapters')
  const [saving, setSaving] = useState(false)
  const [titleError, setTitleError] = useState('')

  useEffect(() => {
    setTitle(book.title)
    setLanguage(book.metadata.language || 'en')
    setVisibility(book.visibility || 'private')
    setDefaultFontFamily(book.settings.defaultFontFamily || 'Huninn')
    setDefaultFontSize(book.settings.defaultFontSize || '12')
    setTitleFontFamily(book.settings.titleFontFamily || 'SBL Hebrew')
    setStructure(book.settings.structure ?? 'chapters')
    setTitleError('')
  }, [book, open])

  const derivedDirection = useMemo(() => {
    return ['he', 'ar'].includes(language) ? 'rtl' : 'ltr'
  }, [language])

  const handleFontFamilyChange = (event: SelectChangeEvent) => {
    setDefaultFontFamily(event.target.value)
  }

  const handleFontSizeChange = (event: SelectChangeEvent) => {
    setDefaultFontSize(event.target.value)
  }

  const handleTitleFontFamilyChange = (event: SelectChangeEvent) => {
    setTitleFontFamily(event.target.value)
  }

  const handleSave = async () => {
    if (!title.trim()) {
      setTitleError('Book title is required')
      return
    }
    setTitleError('')

    try {
      setSaving(true)
      // First time the author enables flat structure: initialize flatSectionsOrder
      // to [] so the Sections view in the TOC unlocks (`flatViewAvailable` keys
      // off `!== undefined`). All sections — existing and future — stay opt-in
      // via the Unplaced pool until the author places them.
      const nextFlatSectionsOrder =
        structure === 'flat' && book.settings.flatSectionsOrder === undefined ? [] : book.settings.flatSectionsOrder
      const response = await apiService.updateBook(book._id, {
        title: title.trim(),
        language,
        visibility,
        settings: {
          autoSave: book.settings.autoSave,
          defaultFontFamily,
          defaultFontSize,
          titleFontFamily,
          structure,
          chapters: book.settings.chapters,
          flatSectionsOrder: nextFlatSectionsOrder,
          // Carried through untouched: this modal writes settings wholesale, and the
          // key is minted server-side — dropping it here would orphan every glyph.
          withheldGlyphKey: book.settings.withheldGlyphKey,
        },
      })

      if (response.success) {
        onBookUpdated(response.data)
        onClose()
      } else {
        toast.error('Failed to update book details')
      }
    } catch (error) {
      logger.error('Error updating book details:', error)
      toast.error('Failed to update book details')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Book Settings</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Book details
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Book title"
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value)
                  if (titleError && event.target.value.trim()) {
                    setTitleError('')
                  }
                }}
                error={!!titleError}
                helperText={titleError || undefined}
              />

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="book-language-label">Language</InputLabel>
                  <Select
                    labelId="book-language-label"
                    value={language}
                    label="Language"
                    onChange={(event) => setLanguage(event.target.value)}
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Direction will be set to {derivedDirection.toUpperCase()}.</FormHelperText>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="book-visibility-label">Visibility</InputLabel>
                  <Select
                    labelId="book-visibility-label"
                    value={visibility}
                    label="Visibility"
                    onChange={(event) => setVisibility(event.target.value as Book['visibility'])}
                  >
                    <MenuItem value="private">Private</MenuItem>
                    <MenuItem value="public">Public</MenuItem>
                    <MenuItem value="paid">Paid</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Book structure
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="book-structure-label">Structure</InputLabel>
              <Select
                labelId="book-structure-label"
                value={structure}
                label="Structure"
                onChange={(event) => setStructure(event.target.value as 'chapters' | 'flat')}
              >
                <MenuItem value="chapters">Chapters (chapters containing sections)</MenuItem>
                <MenuItem value="flat">Sections (a single flat sequence)</MenuItem>
              </Select>
              <FormHelperText>
                Determines what readers see. Authoring uses both views regardless — switch in the sidebar.
              </FormHelperText>
            </FormControl>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
              Editor settings
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="default-font-family-label">Default Font Family</InputLabel>
                <Select
                  labelId="default-font-family-label"
                  value={defaultFontFamily}
                  label="Default Font Family"
                  onChange={handleFontFamilyChange}
                >
                  {FONTS.map((font) => (
                    <MenuItem key={font} value={font} style={{ fontFamily: font }}>
                      {font}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="default-font-size-label">Default Font Size</InputLabel>
                  <Select
                    labelId="default-font-size-label"
                    value={defaultFontSize}
                    label="Default Font Size"
                    onChange={handleFontSizeChange}
                  >
                    {FONT_SIZES.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}pt
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="title-font-family-label">Title Font Family</InputLabel>
                  <Select
                    labelId="title-font-family-label"
                    value={titleFontFamily}
                    label="Title Font Family"
                    onChange={handleTitleFontFamilyChange}
                  >
                    {FONTS.map((font) => (
                      <MenuItem key={font} value={font} style={{ fontFamily: font }}>
                        {font}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default BookSettingsModal
